import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { FaEdit, FaTrash, FaCopy } from 'react-icons/fa'
import toast from 'react-hot-toast'
import { handleDate } from '../handleDateAndPdf'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { deleteBlog, sendBlogMail } from '../api/blogApi'
import Swal from 'sweetalert2'
import DOMPurify from 'dompurify'

const BlogCard = ({ data }) => {
  const queryClient = useQueryClient()
  const [toDelete, setToDelete] = useState(null)
  const [sendingMail, setSendingMail] = useState(false)
  const [expand, setExpand] = useState(false)

  // Send mail mutation with improved loading state
  const sendMailMutation = useMutation({
    mutationFn: (id) => sendBlogMail(id),
    onMutate: () => {
      setSendingMail(true)
    },
    onSuccess: (response) => {
      setSendingMail(false)

      // Check if the response indicates success
      if (response?.status === 200 || response?.data) {
        toast.success(response?.data)
        // Show OTP modal after successful email send
        showOTPModal()
      } else {
        toast.error('Failed to send verification email')
        setToDelete(null)
      }
    },
    onError: (err) => {
      setSendingMail(false)
      console.error('Send mail error:', err)
      toast.error(err.message || 'Failed to send verification email')
      setToDelete(null)
    },
  })

  // Delete mutation with improved success handling
  const deleteMutation = useMutation({
    mutationFn: ({ id, otp }) => deleteBlog(id, otp),
    onSuccess: (response) => {
      // Invalidate and refetch deals
      queryClient.invalidateQueries({ queryKey: ['blog'] })

      // Show success message
      toast.success('Opinion deleted successfully!')

      // Additional success message with SweetAlert
      Swal.fire({
        title: 'Deleted!',
        text: 'The opinion has been deleted successfully.',
        icon: 'success',
        confirmButtonColor: '#3085d6',
        timer: 3000,
        timerProgressBar: true,
      })

      // Reset state
      setToDelete(null)
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete opinion'
      throw new Error(errorMessage)
    },
  })

  // Show OTP modal using SweetAlert with better UX
  const showOTPModal = () => {
    Swal.fire({
      title: 'Enter OTP',
      html: `
          <p>Please enter the OTP sent to your email</p>
          <div style="margin: 10px 0; padding: 10px; background-color: #f0f9ff; border-radius: 8px; border-left: 4px solid #3b82f6;">
            <small style="color: #1e40af;">
              <i class="fas fa-info-circle"></i> Check your email inbox and spam folder
            </small>
          </div>
        `,
      input: 'number',
      inputAttributes: {
        autocapitalize: 'off',
        placeholder: 'Enter 6-digit OTP',
        maxlength: 6,
        style: 'text-align: center; font-size: 18px; letter-spacing: 2px;',
      },
      showCancelButton: true,
      confirmButtonText: 'Delete opinion',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      allowOutsideClick: false,
      inputValidator: (value) => {
        if (!value) {
          return 'Please enter OTP'
        }
        if (value.length !== 6) {
          return 'OTP must be 6 digits'
        }
        if (!/^\d+$/.test(value)) {
          return 'OTP must contain only numbers'
        }
      },
      preConfirm: (otp) => {
        return new Promise((resolve, reject) => {
          // Show loading state
          Swal.showLoading()

          // Update confirm button text while loading
          const confirmButton = Swal.getConfirmButton()
          const originalText = confirmButton.textContent
          confirmButton.textContent = 'Deleting...'

          // Call delete mutation with OTP
          deleteMutation.mutate(
            { id: toDelete, otp },
            {
              onSuccess: () => {
                resolve()
                Swal.close()
              },
              onError: (error) => {
                confirmButton.textContent = originalText
                Swal.hideLoading()
                const errorMessage =
                  error?.response?.data?.message ||
                  error?.message ||
                  'Invalid OTP or deletion failed'
                Swal.showValidationMessage(`❌ ${errorMessage}`)
                reject(error)
              },
            }
          )
        })
      },
    })
      .then((result) => {
        if (result.dismiss === Swal.DismissReason.cancel) {
          setToDelete(null)
          toast.info('Deletion cancelled')
        }
      })
      .catch(() => {
        // Handle any unexpected errors
        setToDelete(null)
      })
  }

  // Handle delete click with improved UX
  const handleDeleteClick = (dealId, dealTitle) => {
    setToDelete(dealId)

    // Show confirmation dialog first
    Swal.fire({
      title: 'Delete opinion?',
      html: `
          <p>Are you sure you want to delete this opinion?</p>
          <div style="margin: 10px 0; padding: 10px; background-color: #fef2f2; border-radius: 8px; border-left: 4px solid #ef4444;">
            <strong style="color: #dc2626;">${dealTitle}</strong>
          </div>
          <p style="color: #6b7280; font-size: 14px;">
            This action will send an OTP to your email for verification.
          </p>
        `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Send OTP',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        // Send email for OTP
        sendMailMutation.mutate(dealId)
      } else {
        setToDelete(null)
      }
    })
  }

  const handleCopy = (link) => {
    navigator.clipboard.writeText(link)
    toast.success('Link copied!')
  }

  return (
    <>
      {/* {console.log('preseed', data)} */}
      {data.map((d) => {
        const date = new Date(d.articleDate)
        d.articleDate = date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
        return (
          <div
            key={d.id}
            className="bg-white text-left shadow-md rounded-lg p-4 w-full h-fit sm:w-[48%]"
          >
            {/* brand name and writtenBy */}
            <div className="flex justify-between items-center">
              <h3 className="text-xl text-[#4E4A4A] font-semibold mb-2">
                {d.brandName}
              </h3>
              {d?.writtenBy && (
                <p className="text-sm text-gray-800 mb-2">
                  By <span className="text-gray-500">{d.writtenBy}</span>
                </p>
              )}
            </div>

            {/* cin and article date */}
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500 mb-2">
                <span className="font-aptos-semibold text-gray-700">
                  Article Date&nbsp;:&nbsp;
                </span>
                {d.articleDate}
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center w-fit justify-between bg-gray-100 p-2 rounded mb-2">
                <span className="text-sm text-orange-600 truncate block max-w-[100px]">{`/blog/${d.slug}`}</span>
                <button
                  onClick={() => handleCopy(`/blog/${d.slug}`)}
                  className="text-gray-600 hover:text-orange-600 ml-2"
                >
                  <FaCopy />
                </button>
              </div>
              <div className="flex items-center w-fit justify-between bg-gray-100 p-2 rounded mb-2">
                <span className="text-sm text-gray-500 break-all">
                  Category :&nbsp;
                </span>
                {d?.categoryName || 'N/A'}
              </div>
            </div>

            <p className="text-[#4E4A4A] font-semibold mb-2 inline-block">
              {d.title}
            </p>
            <img
              src={d.imageUrl}
              alt={d.brandName}
              className="w-full h-60 object-cover rounded-md mb-1 border-gray-400 border-2"
            />

            {d?.pcCredit && (
              <p className="text-sm text-gray-500 mb-3 italic flex items-center justify-end">
                <span className="font-aptos-semibold text-gray-500">
                  Source&nbsp;:&nbsp;
                </span>
                {d.pcCredit}
              </p>
            )}

            <p
              className={`text-gray-500 text-sm break-words whitespace-wrap ${
                expand === d.id ? '' : 'line-clamp-6'
              } `}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(d.description),
              }}
            ></p>

            {d.id === expand ? (
              <p
                className="text-[#e15d05] cursor-pointer float-right"
                onClick={() => setExpand(false)}
              >
                View less
              </p>
            ) : (
              <p
                className="text-[#e15d05] cursor-pointer float-right"
                onClick={() => setExpand(d.id)}
              >
                View full
              </p>
            )}

            <div className="flex justify-between items-center mb-1 mt-5">
              <div>
                <p>Created At</p>
                <div className="flex items-center justify-left bg-gray-100 p-2 rounded mb-3 w-fit">
                  <span className="text-sm text-gray-500 break-all">
                    {handleDate(d.createdAt)}
                  </span>
                </div>

                <p>Created By</p>
                <div className="flex items-center justify-left bg-gray-100 p-2 rounded mb-3 w-fit">
                  <span className="text-sm text-gray-500 break-all">
                    {d?.createAdmin}
                  </span>
                </div>
              </div>
              {d.updatedAt && (
                <div className=" mb-3">
                  <p>Updated At</p>
                  <div className="flex items-center justify-left bg-gray-100 p-2 rounded mb-3 w-fit">
                    <span className="text-sm text-gray-500 break-all">
                      {handleDate(d.updatedAt)}
                    </span>
                  </div>

                  <p>Updated By</p>
                  <div className="flex items-center justify-left bg-gray-100 p-2 rounded mb-3 w-fit">
                    <span className="text-sm text-gray-500 break-all">
                      {d?.updateAdmin}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between space-x-4">
              <Link
                to={`/dealdashboard/add/blog/${d.id}`}
                className="text-green-600 hover:text-green-800 flex items-center"
              >
                <FaEdit className="mr-1" /> Edit
              </Link>
              <button
                type="button"
                onClick={() => handleDeleteClick(d.id, d.brandName)}
                className={`text-red-500 hover:text-red-700 transition-colors p-1 ${
                  sendingMail || deleteMutation.isPending
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
                title="Delete Slider"
                disabled={sendingMail || deleteMutation.isPending}
              >
                {sendingMail && toDelete === d.id ? (
                  <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                ) : (
                  <FaTrash />
                )}
              </button>
            </div>
          </div>
        )
      })}
      {/* Loading overlay for sending email */}
      {sendingMail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl text-center">
            <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-lg font-semibold">
              Sending verification email...
            </p>
            <p className="text-sm text-gray-500 mt-1">Please wait</p>
          </div>
        </div>
      )}
    </>
  )
}

export default BlogCard
